import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { TimeSeriesData } from '../../types/sop';
import './TimeSeriesChart.css';

interface TimeSeriesChartProps {
  data: TimeSeriesData[];
  metrics: string[];
  colors: string[];
  title: string;
  width?: number;
  height?: number;
}

const TimeSeriesChart: React.FC<TimeSeriesChartProps> = ({
  data,
  metrics,
  colors,
  title,
  width = 400,
  height = 300
}) => {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!data.length || !svgRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove(); // Clear previous chart

    const margin = { top: 20, right: 80, bottom: 40, left: 60 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    const g = svg
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Parse week dates and sort data
    const parseWeek = (weekStr: string) => {
      const [year, week] = weekStr.split('-W');
      const date = new Date(parseInt(year), 0, 1 + (parseInt(week) - 1) * 7);
      return date;
    };

    const sortedData = [...data].sort((a, b) => 
      parseWeek(a.week_date).getTime() - parseWeek(b.week_date).getTime()
    );

    // Set up scales
    const xScale = d3.scaleTime()
      .domain(d3.extent(sortedData, d => parseWeek(d.week_date)) as [Date, Date])
      .range([0, innerWidth]);

    const allValues = sortedData.flatMap(d => 
      metrics.map(metric => (d as any)[metric] || 0)
    );
    
    const yScale = d3.scaleLinear()
      .domain([0, d3.max(allValues) || 0])
      .nice()
      .range([innerHeight, 0]);

    // Create line generator
    const line = d3.line<TimeSeriesData>()
      .x(d => xScale(parseWeek(d.week_date)))
      .y((d, i, data) => {
        // This will be set for each metric separately
        return yScale(0);
      })
      .curve(d3.curveMonotoneX);

    // Add axes
    const xAxis = d3.axisBottom(xScale)
      .tickFormat(d3.timeFormat('%Y-W%U') as any);
    
    g.append('g')
      .attr('class', 'x-axis')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(xAxis)
      .selectAll('text')
      .style('text-anchor', 'end')
      .attr('dx', '-.8em')
      .attr('dy', '.15em')
      .attr('transform', 'rotate(-45)');

    const yAxis = d3.axisLeft(yScale);
    g.append('g')
      .attr('class', 'y-axis')
      .call(yAxis);

    // Add grid lines
    g.append('g')
      .attr('class', 'grid')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(d3.axisBottom(xScale)
        .tickSize(-innerHeight)
        .tickFormat(() => '')
      );

    g.append('g')
      .attr('class', 'grid')
      .call(d3.axisLeft(yScale)
        .tickSize(-innerWidth)
        .tickFormat(() => '')
      );

    // Draw lines for each metric
    metrics.forEach((metric, index) => {
      const metricLine = d3.line<TimeSeriesData>()
        .x(d => xScale(parseWeek(d.week_date)))
        .y(d => yScale((d as any)[metric] || 0))
        .curve(d3.curveMonotoneX);

      g.append('path')
        .datum(sortedData)
        .attr('class', 'line')
        .attr('d', metricLine)
        .style('stroke', colors[index] || '#2196F3')
        .style('stroke-width', 2)
        .style('fill', 'none');

      // Add dots for data points
      g.selectAll(`.dot-${index}`)
        .data(sortedData)
        .enter().append('circle')
        .attr('class', `dot dot-${index}`)
        .attr('cx', d => xScale(parseWeek(d.week_date)))
        .attr('cy', d => yScale((d as any)[metric] || 0))
        .attr('r', 4)
        .style('fill', colors[index] || '#2196F3')
        .style('stroke', 'white')
        .style('stroke-width', 2);

      // Add hover effects
      g.selectAll(`.dot-${index}`)
        .on('mouseover', function(event: any, d: any) {
          const tooltip = d3.select('body').append('div')
            .attr('class', 'chart-tooltip')
            .style('opacity', 0);

          tooltip.transition()
            .duration(200)
            .style('opacity', .9);
          
          tooltip.html(`
            <strong>${d.week_date}</strong><br/>
            ${metric.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}: ${(d[metric] || 0).toLocaleString()}
          `)
            .style('left', (event.pageX + 10) + 'px')
            .style('top', (event.pageY - 28) + 'px');
        })
        .on('mouseout', function() {
          d3.selectAll('.chart-tooltip').remove();
        });
    });

    // Add legend
    const legend = g.append('g')
      .attr('class', 'legend')
      .attr('transform', `translate(${innerWidth + 10}, 20)`);

    metrics.forEach((metric, index) => {
      const legendItem = legend.append('g')
        .attr('transform', `translate(0, ${index * 20})`);

      legendItem.append('rect')
        .attr('width', 12)
        .attr('height', 12)
        .style('fill', colors[index] || '#2196F3');

      legendItem.append('text')
        .attr('x', 18)
        .attr('y', 9)
        .attr('dy', '.35em')
        .style('font-size', '12px')
        .text(metric.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()));
    });

  }, [data, metrics, colors, width, height]);

  return (
    <div className="time-series-chart">
      <svg
        ref={svgRef}
        width={width}
        height={height}
        className="chart-svg"
      />
    </div>
  );
};

export default TimeSeriesChart;